/**
 * checkUserService.ts
 *
 * Cognito認証後にDynamoDBユーザーの存在確認・作成を行う純粋関数。
 * hookに依存せず、AwsAuthContext.tsxのlogin()内から呼び出し可能。
 */
import { gql } from '@apollo/client';
import {
  CreateUserMutation,
  CreateUserMutationVariables,
  DeleteStatus,
  GetUserQuery,
  ListOrganizationsByNameQuery,
  ListOrganizationsByNameQueryVariables,
  ModelSortDirection,
  Organization,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  User,
  UserStatus,
} from 'src/API';
import { CognitoUserInterface } from 'src/@types/types';
import { client } from './apolloClient';
import { store } from 'src/redux/store';
import appSlice from 'src/redux/slices/appSlice';
import { addToGroup } from './apiTypeUtil';
import * as queries from 'src/graphql/queries';
import * as mutations from 'src/graphql/mutations';

// Helper: convert base64url (JWT) to base64 and decode safely
const decodeJwtPayload = (base64UrlSegment: string): Record<string, unknown> => {
  const base64 = base64UrlSegment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const json = atob(padded);
  return JSON.parse(json);
};

// Helper function to get groups from JWT token
const getGroupsFromToken = (idToken: string | null): string[] => {
  try {
    if (!idToken || typeof idToken !== 'string') {
      return [];
    }
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return [];
    }
    const payload = decodeJwtPayload(parts[1]);
    return (payload['cognito:groups'] as string[]) || [];
  } catch (error) {
    console.error('Failed to decode token:', error);
    return [];
  }
};

// Helper function to check if user is in a specific group
const checkGroup = (groupName: string): boolean => {
  const idToken = localStorage.getItem('idToken');
  if (!idToken) return false;
  const groups = getGroupsFromToken(idToken);
  return groups.includes(groupName);
};

export interface CheckUserServiceResult {
  success: boolean;
  user: User | null;
  organization: Organization | null;
  error?: string;
  errorType?: 'ORGANIZATION_NOT_FOUND' | 'USER_DELETED' | 'CREATE_FAILED' | 'UNKNOWN';
}

/**
 * DynamoDBからユーザーを取得する
 */
async function fetchUser(userId: string): Promise<User | null> {
  try {
    const result = await client.query<GetUserQuery>({
      query: gql(queries.getUser),
      variables: { id: userId },
      fetchPolicy: 'network-only',
    });
    return (result.data?.getUser as User) ?? null;
  } catch (error) {
    console.error('Failed to fetch user:', error);
    return null;
  }
}

/**
 * 組織を名前で検索する
 */
async function fetchOrganizationByName(organizationName: string): Promise<Organization | null> {
  try {
    const result = await client.query<
      ListOrganizationsByNameQuery,
      ListOrganizationsByNameQueryVariables
    >({
      query: gql(queries.listOrganizationsByName),
      variables: {
        name: organizationName,
        limit: 1000,
        sortDirection: ModelSortDirection.DESC,
      },
      fetchPolicy: 'network-only',
    });
    const organizations = result.data?.listOrganizationsByName?.items;
    if (!organizations || organizations.length === 0) {
      return null;
    }
    return organizations[0] as Organization;
  } catch (error) {
    console.error('Failed to fetch organization:', error);
    return null;
  }
}

/**
 * DynamoDBにユーザーを作成する
 */
async function createUserInDynamoDB(
  cognito: CognitoUserInterface,
  organization: Organization,
  isAdmin: boolean
): Promise<User | null> {
  const username = cognito.username;
  if (!username) {
    console.error('Cannot create user: username is missing');
    return null;
  }

  try {
    const input = {
      id: username,
      account: username,
      sub: cognito.attributes?.sub,
      organizationID: organization.id,
      organizationGroup: organization.organizationGroup,
      adminGroup: organization.adminGroup,
      email: cognito.attributes.email,
      firstName: cognito.attributes.given_name ?? cognito.attributes['custom:firstName'],
      lastName: cognito.attributes.family_name ?? cognito.attributes['custom:lastName'],
      admin: isAdmin,
      status: cognito.attributes['custom:teamID']
        ? UserStatus.ActivateComplete
        : UserStatus.Invited,
      teamID: cognito.attributes['custom:teamID'],
      deleteStatus: DeleteStatus.notDeleted,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const result = await client.mutate<CreateUserMutation, CreateUserMutationVariables>({
      mutation: gql(mutations.createUser),
      variables: { input },
    });

    return (result.data?.createUser as User) ?? null;
  } catch (error) {
    console.error('Failed to create user in DynamoDB:', error);
    return null;
  }
}

/**
 * ユーザーのステータスをActivateCompleteに更新する
 */
async function updateUserStatus(userId: string): Promise<void> {
  try {
    await client.mutate<UpdateUserMutation, UpdateUserMutationVariables>({
      mutation: gql(mutations.updateUser),
      variables: {
        input: {
          id: userId,
          status: UserStatus.ActivateComplete,
          updatedAt: new Date().toISOString(),
        },
      },
    });
  } catch (error) {
    console.error('Failed to update user status:', error);
  }
}

/**
 * checkUserService
 *
 * Cognito認証後にDynamoDBユーザーの存在確認・作成を行う。
 * hookに依存しない純粋関数として実装。
 *
 * @param cognito - Cognitoユーザー情報
 * @returns CheckUserServiceResult
 */
export async function checkUserService(
  cognito: CognitoUserInterface
): Promise<CheckUserServiceResult> {
  if (!cognito?.username) {
    return {
      success: false,
      user: null,
      organization: null,
      error: 'Cognito user information is missing',
      errorType: 'UNKNOWN',
    };
  }

  const isGlobalAdmin = checkGroup('GlobalAdmins');
  const isAdmin = checkGroup('Admins');
  const isAdminGroup = checkGroup(`admin${cognito.attributes['custom:organization']}`);

  // Redux storeにadmin情報を設定
  store.dispatch(appSlice.actions.setAdmin({ isGlobalAdmin, isAdmin }));

  // globalAdminの場合は特別処理
  if (cognito.username === 'globalAdmin') {
    if (isGlobalAdmin && isAdmin) {
      store.dispatch(appSlice.actions.setGlobalMode(isGlobalAdmin));
      store.dispatch(appSlice.actions.setUser(null));
      return {
        success: true,
        user: null,
        organization: null,
      };
    } else {
      return {
        success: false,
        user: null,
        organization: null,
        error: 'globalAdmin に権限がありません。',
        errorType: 'UNKNOWN',
      };
    }
  }

  // 管理者の場合、AdminsとAdminGroupはセットで所属している必要がある
  if (isAdmin && !isAdminGroup) {
    try {
      await addToGroup(cognito.username ?? '', `admin${cognito.attributes['custom:organization']}`);
    } catch (error) {
      console.error('Failed to add user to admin group:', error);
    }
  }

  // 組織を取得
  const organizationName = cognito.attributes['custom:organization'];
  const organization = await fetchOrganizationByName(organizationName);

  if (!organization) {
    localStorage.clear();
    return {
      success: false,
      user: null,
      organization: null,
      error: `組織「${organizationName}」が見つかりません。`,
      errorType: 'ORGANIZATION_NOT_FOUND',
    };
  }

  // Redux storeに組織情報を設定
  store.dispatch(appSlice.actions.setOrganization(organization));

  // DynamoDBからユーザーを取得
  const existingUser = await fetchUser(cognito.username);

  if (existingUser) {
    // ユーザーが存在する場合

    // 削除されたユーザーの場合
    if (existingUser.deleteStatus === DeleteStatus.deleted) {
      store.dispatch(
        appSlice.actions.setSnackbarMessage({
          message: 'ログインに失敗しました。このアカウントは管理者によって停止されています。',
          type: 'error',
        })
      );
      return {
        success: false,
        user: null,
        organization,
        error: 'このアカウントは管理者によって停止されています。',
        errorType: 'USER_DELETED',
      };
    }

    // 招待済みステータスの場合、ActivateCompleteに更新
    if (existingUser.status === UserStatus.Invited) {
      await updateUserStatus(existingUser.id);
    }

    // Redux storeにユーザー情報を設定
    store.dispatch(appSlice.actions.setUser(existingUser as User));

    return {
      success: true,
      user: existingUser as User,
      organization,
    };
  }

  // ユーザーが存在しない場合、新規作成
  const createdUser = await createUserInDynamoDB(cognito, organization, isAdmin);

  if (!createdUser) {
    return {
      success: false,
      user: null,
      organization,
      error: 'ユーザーの作成に失敗しました。',
      errorType: 'CREATE_FAILED',
    };
  }

  // Redux storeにユーザー情報を設定
  store.dispatch(appSlice.actions.setUser(createdUser as User));

  return {
    success: true,
    user: createdUser as User,
    organization,
  };
}
